import { Directive, ElementRef, HostListener, Input } from '@angular/core';

import { DepositCtaAnalyticsService, DepositEntryPoint } from '../services/deposit-cta-analytics.service';

/**
 * Fires `Deposit CTA Clicked` for a button or link that sends the user into the deposit flow.
 *
 * Applied alongside `routerLink` rather than replacing it — navigation stays the router's
 * job, so middle-click and ctrl-click keep opening a new tab as they do today, and a
 * failure in tracking can never strand a user on the page they clicked from.
 *
 * Pair it with the matching `[queryParams]="depositEntryParams('<id>')"` on the same
 * element so the arrival is labelled with the same entry point as the click:
 *
 *   <a [routerLink]="['/deposit-asset']"
 *      [queryParams]="depositEntryParams('header')"
 *      appDepositCta="header">Deposit asset.</a>
 */
@Directive({
    selector: '[appDepositCta]',
    standalone: true,
})
export class DepositCtaDirective {
    /** Which CTA this is. Sent as `entry_point`, and must match the element's `from` param. */
    @Input('appDepositCta') entryPoint!: DepositEntryPoint;

    constructor(
        private depositCtaAnalytics: DepositCtaAnalyticsService,
        private elementRef: ElementRef<HTMLElement>,
    ) {}

    /**
     * Tracked on click rather than on navigation so a signed-out user still counts: they
     * are redirected to sign-in and never reach a `Deposit Started`.
     */
    @HostListener('click')
    onClick(): void {
        if (!this.entryPoint) return;
        this.depositCtaAnalytics.ctaClicked(this.entryPoint, this.readLabel());
    }

    /**
     * The button's visible text, so a report is readable without someone having to look up
     * what `dashboard-checklist` points at. Inline SVG icons contribute nothing here —
     * their path data lives in attributes, not text nodes.
     */
    private readLabel(): string {
        return this.elementRef.nativeElement?.textContent ?? '';
    }
}
